import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Switch,
  Typography,
} from '@mui/material';

import asyncService from '../../../datahub/asyncService';
import { dispatchErrors } from '../../../datahub/errorUtils';
import KeyboardArrowDownIcon from '../../assets/icons/KeyboardArrowDownIcon';
import selectUser from '../../store/selectors/appSelector';
import { BLACK } from '../../theme/colorConstants';
import { PRIMARY_FONT } from '../../theme/fontConstants';
import { showSnackbar } from '../../views/app';
import Button from '../Button';

import LocationSelectionList from './LocationSelectionList';
import PositionTemplateSelectionList from './PositionTemplateSelectionList';

const AgencyCorporationSupportSetter = ({
  corpName,
  corpId,
  agencyId,
  currentlySetLocationIds,
  currentlySetPositionTemplateIds,
}) => {
  const dispatch = useDispatch();
  const { token, refreshToken } = useSelector(selectUser);
  const [isExpanded, setIsExpanded] = useState(false);

  const [positionTemplateData, _setPositionTemplateDataPrivate] = useState([]);
  const setPositionTemplateData = (newValue) =>
    _setPositionTemplateDataPrivate(
      newValue.map(
        (x) =>
          // NOTE: This is a REAL undefined check. It's not just looking for falsey!
          (x.isSelected === undefined &&
            currentlySetPositionTemplateIds.includes(x._id) && { ...x, isSelected: true }) ||
          x
      )
    );
  const [locationData, _setLocationDataPrivate] = useState([]);
  const setLocationData = (newValue) =>
    _setLocationDataPrivate(
      newValue.map(
        (x) =>
          // NOTE: This is a REAL undefined check. It's not just looking for falsey!
          (x.isSelected === undefined &&
            currentlySetLocationIds.includes(x._id) && { ...x, isSelected: true }) ||
          x
      )
    );
  const apiUpdateAgencySupportAtCorporation = (payload) =>
    asyncService({
      httpMethod: 'PATCH',
      route: 'agency/update-corporation-support',
      data: payload,
      onSuccess: () => {
        dispatch(showSnackbar({ message: 'dashboard.agency.edit.updateCorporationsSuccess' }));
      },
      onError: () => {
        dispatchErrors({ dispatch, responsePayload: payload });
      },
      dispatch,
      token,
      refreshToken,
    });

  const handleAccordionChange = () => (_, newVal) => {
    setIsExpanded(newVal);
  };
  const handleOnSelectAllPositionsChange = (event) => {
    setPositionTemplateData(
      positionTemplateData.map((x) => ({
        ...x,
        isSelected: event.target.checked,
      }))
    );
  };
  const handleOnSelectAllLocationsChange = (event) => {
    setLocationData(
      locationData.map((x) => ({
        ...x,
        isSelected: event.target.checked,
      }))
    );
  };
  return (
    <Accordion
      key={corpName}
      expanded={isExpanded}
      onChange={handleAccordionChange()}
      defaultExpanded={false}
    >
      <AccordionSummary
        aria-controls="department-content"
        id="agencies-corporation-support-header"
        expandIcon={<KeyboardArrowDownIcon />}
        sx={{ background: BLACK[10] }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Typography
            sx={{
              alignSelf: 'center',
              fontSize: '16px',
              marginRight: '12px',
              fontFamily: PRIMARY_FONT[700],
            }}
          >
            {corpName}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Box
            sx={{
              flex: 1,
              p: 1,
            }}
          >
            <Typography
              sx={{
                alignSelf: 'center',
                fontSize: '20px',
                marginRight: '12px',
                fontFamily: PRIMARY_FONT[700],
                p: 1.5,
              }}
            >
              {LanguageConverterV2('dashboard.agency.edit.corporationLocations')}
            </Typography>
            <Box sx={{ width: '100%', display: 'flex' }} flexDirection="row" alignItems="center">
              <Switch sx={{ p: '12px' }} onChange={handleOnSelectAllLocationsChange} />
              <Typography
                sx={{
                  fontSize: '20px',
                  marginRight: '12px',
                  fontFamily: PRIMARY_FONT[400],
                }}
              >
                {LanguageConverterV2('dashboard.agency.edit.corporationSelectAll')}
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <LocationSelectionList
                corpId={corpId}
                isReadyToLoad={isExpanded}
                locationData={locationData}
                setLocationData={setLocationData}
              />
            </Box>
          </Box>
          <Divider orientation="vertical" flexItem sx={{ borderRightWidth: '2px' }} />
          <Box
            sx={{
              flex: 1,
              p: 1,
            }}
          >
            <Typography
              sx={{
                alignSelf: 'center',
                fontSize: '20px',
                marginRight: '12px',
                fontFamily: PRIMARY_FONT[700],
                p: 1.5,
              }}
            >
              {LanguageConverterV2('dashboard.agency.edit.corporationPositions')}
            </Typography>
            <Box sx={{ width: '100%', display: 'flex' }} flexDirection="row" alignItems="center">
              <Switch sx={{ p: '12px' }} onChange={handleOnSelectAllPositionsChange} />
              <Typography
                sx={{
                  fontSize: '20px',
                  marginRight: '12px',
                  fontFamily: PRIMARY_FONT[400],
                }}
              >
                {LanguageConverterV2('dashboard.agency.edit.corporationSelectAll')}
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <PositionTemplateSelectionList
                corpId={corpId}
                isReadyToLoad={isExpanded}
                positionTemplateData={positionTemplateData}
                setPositionTemplateData={setPositionTemplateData}
              />
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: '16px', background: BLACK[10] }}>
          <Button
            data-testid="agency-corp-support-setter-submit-button"
            text={LanguageConverterV2('buttonText.submit')}
            onClick={() => {
              const payload = {
                agencyId,
                corpId,
                positionTemplates: positionTemplateData
                  .map((pt) => pt.isSelected && pt._id)
                  .filter(Boolean),
                locations: locationData.map((loc) => loc.isSelected && loc._id).filter(Boolean),
              };

              return apiUpdateAgencySupportAtCorporation(payload);
            }}
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

AgencyCorporationSupportSetter.propTypes = {
  agencyId: PropTypes.string,
  corpId: PropTypes.string,
  corpName: PropTypes.string,
  currentlySetLocationIds: PropTypes.arrayOf(PropTypes.string),
  currentlySetPositionTemplateIds: PropTypes.arrayOf(PropTypes.string),
};

export default AgencyCorporationSupportSetter;
